import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styles from './AuthForm.module.css';
import { KEY_CODE_SAPCE } from '../../constants/NumberConstants';
import { getPasswordPolicyChecks, MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from '@shared/utils/password-policy';

const ConfirmForgotPassword = ({
  onLoginClick = () => {},
  handleConfirmPassword = () => {},
  resetPasswordStatus = { text: '' },
  isQueryEmail,
}) => {
  const [formVal, setFormVal] = useState({
    password: '',
    retypepassword: '',
    resetPasswordStatus: '',
  });
  const retypeInputRef = useRef(null);

  const { password, retypepassword } = formVal;
  const checks = getPasswordPolicyChecks(password);
  const isPassMatch = password.length > 0 && retypepassword.length > 0 && password === retypepassword;
  const canSubmit = checks.valid && isPassMatch;

  useEffect(() => {
    const el = retypeInputRef.current;
    if (!el) return;
    if (retypepassword.length > 0 && password !== retypepassword) {
      el.setCustomValidity("Password and confirm password don't match");
    } else {
      el.setCustomValidity('');
    }
  }, [password, retypepassword]);

  const handleSpace = (e) => {
    if (e.keyCode === KEY_CODE_SAPCE) {
      e.preventDefault();
    }
  };

  const inputEvent = (e) => {
    const { name, value } = e.target;
    const next = name === 'password' || name === 'retypepassword' ? String(value).replace(/\s/g, '') : value;
    if (next !== value) {
      e.target.value = next;
    }
    setFormVal((preValue) => ({
      ...preValue,
      [name]: next,
    }));
  };

  const formSubmit = (events) => {
    events.preventDefault();
    if (!canSubmit) {
      return;
    }
    const { search } = window.location;
    const params = new URLSearchParams(search);
    const data = {
      password: formVal.password,
      resetcode: params.get('key'),
      email: params.get('email'),
    };
    handleConfirmPassword(data);
  };

  return (
    <div className={styles.loginForgetPassword}>
      <div>
        <form action="" method="get" noValidate onSubmit={formSubmit}>
          <fieldset className={styles.clearfix}>
            <h1> {isQueryEmail ? 'Setup' : 'Reset'} Password</h1>
            <span className="float-start text-start mb-2">Enter new password.</span>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon2">
                <svg
                  width="1em"
                  height="1em"
                  viewBox="0 0 16 16"
                  className="bi bi-lock-fill"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M2.5 9a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2V9z" />
                  <path fillRule="evenodd" d="M4.5 4a3.5 3.5 0 0 1 7 0v3h-1V4a2.5 2.5 0 0 0-5 0v3h-1V4z" />
                </svg>
              </span>
              <input
                type="password"
                name="password"
                onChange={inputEvent}
                onKeyDown={handleSpace}
                className="form-control"
                placeholder="New Password"
                aria-label="New-Password"
                aria-describedby="basic-addon2"
                minLength={MIN_PASSWORD_LENGTH}
                maxLength={MAX_PASSWORD_LENGTH}
                autoComplete="new-password"
                required
              />
            </div>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon2-retype">
                <svg
                  width="1em"
                  height="1em"
                  viewBox="0 0 16 16"
                  className="bi bi-lock-fill"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M2.5 9a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2V9z" />
                  <path fillRule="evenodd" d="M4.5 4a3.5 3.5 0 0 1 7 0v3h-1V4a2.5 2.5 0 0 0-5 0v3h-1V4z" />
                </svg>
              </span>
              <input
                ref={retypeInputRef}
                type="password"
                name="retypepassword"
                onChange={inputEvent}
                onKeyDown={handleSpace}
                className="form-control"
                placeholder="Retype New Password"
                aria-label="Retype-New-Password"
                aria-describedby="basic-addon2-retype"
                minLength={MIN_PASSWORD_LENGTH}
                maxLength={MAX_PASSWORD_LENGTH}
                autoComplete="new-password"
                required
              />
            </div>
            <div>
              <span className="float-start" onClick={onLoginClick}>
                <button type="button" className="btn btn-link">
                  Back to login?
                </button>
              </span>
              <span className={styles.spanSubmit}>
                <button
                  className="btn btn-primary float-end"
                  type="submit"
                  disabled={!canSubmit}
                  title={!canSubmit ? 'Complete all password requirements to continue' : undefined}
                >
                  {isQueryEmail ? 'Create' : 'Update'} Password
                </button>
              </span>
            </div>
            <div style={{ color: 'red' }}>{resetPasswordStatus ? resetPasswordStatus.text : ''}</div>
          </fieldset>
        </form>
      </div>
      <div className={styles.require}>
        <span>Password must meet the following:</span>
        <div className="mt-2">
          <div className={checks.lengthInRange ? styles.isValidPassword : styles.isInvalidPassword}>
            <i
              className={
                checks.lengthInRange
                  ? 'bi bi-check-circle-fill  passChar_8_icon'
                  : 'bi bi-x-circle-fill  passChar_8_icon'
              }
            />{' '}
            is between {MIN_PASSWORD_LENGTH} and {MAX_PASSWORD_LENGTH} characters
          </div>
          <div className={checks.notWeak ? styles.isValidPassword : styles.isInvalidPassword}>
            <i
              className={
                checks.notWeak ? 'bi bi-check-circle-fill  passWeak_icon' : 'bi bi-x-circle-fill  passWeak_icon'
              }
            />{' '}
            not a commonly used or easily guessed password
          </div>
          <div className={checks.mixedCase ? styles.isValidPassword : styles.isInvalidPassword}>
            <i
              className={
                checks.mixedCase ? 'bi bi-check-circle-fill  passChar_1_icon' : 'bi bi-x-circle-fill  passChar_1_icon'
              }
            />{' '}
            uppercase and lowercase letters
          </div>
          <div className={checks.hasDigit ? styles.isValidPassword : styles.isInvalidPassword}>
            <i
              className={
                checks.hasDigit ? 'bi bi-check-circle-fill  passNum_1_icon' : 'bi bi-x-circle-fill  passNum_1_icon'
              }
            />{' '}
            at least one number
          </div>
          <div className={checks.hasSpecial ? styles.isValidPassword : styles.isInvalidPassword}>
            <i
              className={
                checks.hasSpecial
                  ? 'bi bi-check-circle-fill  passSP_Char_1_icon'
                  : 'bi bi-x-circle-fill  passSP_Char_1_icon'
              }
            />{' '}
            at least one special character (!, @, #, $, %, etc.)
          </div>
          <div className={isPassMatch ? styles.isValidPassword : styles.isInvalidPassword}>
            <i
              className={
                isPassMatch ? 'bi bi-check-circle-fill  passBoxMatch_icon' : 'bi bi-x-circle-fill   passBoxMatch_icon'
              }
            />{' '}
            match in both boxes
          </div>
        </div>
      </div>
    </div>
  );
};

ConfirmForgotPassword.propTypes = {
  handleConfirmPassword: PropTypes.func,
  onLoginClick: PropTypes.func,
  resetPasswordStatus: PropTypes.shape({
    text: PropTypes.string,
  }),
};

export default React.memo(ConfirmForgotPassword);
